/**
 * モーダル
 */
export const MODAL = {
  selectors: {
    modal: '.js-modal',
    open: '.js-modal-open',
    close: '.js-modal-close',
    overlay: '.js-modal-overlay',
    focusable: [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(','),
  },

  classNames: {
    open: 'is-open',
    bodyFixed: 'is-modal-open',
  },

  currentModal: null,
  lastFocusedElement: null,
  scrollPosition: 0,

  init() {
    const openTriggers = document.querySelectorAll(this.selectors.open)
    const modals = document.querySelectorAll(this.selectors.modal)

    if (!modals.length) {
      return
    }

    openTriggers.forEach(trigger => {
      trigger.addEventListener('click', event => {
        event.preventDefault()

        const targetName = trigger.dataset.modalTarget

        if (!targetName) {
          return
        }

        const modal = this.findModal(modals, targetName)

        if (!modal) {
          return
        }

        this.open(modal, trigger)
      })
    })

    modals.forEach(modal => {
      const closeButtons = modal.querySelectorAll(this.selectors.close)
      const overlays = modal.querySelectorAll(this.selectors.overlay)

      closeButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.close(modal)
        })
      })

      overlays.forEach(overlay => {
        overlay.addEventListener('click', () => {
          this.close(modal)
        })
      })
    })

    document.addEventListener('keydown', event => {
      this.handleKeydown(event)
    })

    this.openFromUrl(modals)
  },

  findModal(modals, targetName) {
    return Array.from(modals).find(item => item.dataset.modal === targetName)
  },

  openFromUrl(modals) {
    const params = new URLSearchParams(window.location.search)
    const targetName = params.get('modal-target')

    if (!targetName) {
      return
    }

    const modal = this.findModal(modals, targetName)

    if (!modal) {
      return
    }

    this.open(modal)
  },

  open(modal, trigger) {
    if (this.currentModal) {
      this.close(this.currentModal, false)
    }

    this.currentModal = modal
    this.lastFocusedElement = trigger

    modal.hidden = false
    // modal.setAttribute('aria-hidden', 'false')

    this.lockScroll()

    requestAnimationFrame(() => {
      modal.classList.add(this.classNames.open)

      const closeButton = modal.querySelector(this.selectors.close)
      const focusableElements = this.getFocusableElements(modal)

      if (closeButton) {
        closeButton.focus()
        return
      }

      if (focusableElements.length) {
        focusableElements[0].focus()
        return
      }

      modal.setAttribute('tabindex', '-1')
      modal.focus()
    })
  },

  close(modal, restoreFocus = true) {
    if (!modal || modal.hidden) {
      return
    }

    modal.classList.remove(this.classNames.open)
    // modal.setAttribute('aria-hidden', 'true')

    const finishClose = () => {
      modal.hidden = true
      modal.removeEventListener('transitionend', handleTransitionEnd)
    }

    const handleTransitionEnd = event => {
      if (event.target !== modal) {
        return
      }

      finishClose()
    }

    modal.addEventListener('transitionend', handleTransitionEnd)

    window.setTimeout(() => {
      if (!modal.classList.contains(this.classNames.open)) {
        finishClose()
      }
    }, 400)

    this.unlockScroll()

    if (restoreFocus && this.lastFocusedElement) {
      this.lastFocusedElement.focus()
    }

    this.currentModal = null
    this.lastFocusedElement = null
  },

  handleKeydown(event) {
    if (!this.currentModal) {
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      this.close(this.currentModal)
      return
    }

    if (event.key === 'Tab') {
      this.trapFocus(event)
    }
  },

  trapFocus(event) {
    const focusableElements = this.getFocusableElements(this.currentModal)

    if (!focusableElements.length) {
      event.preventDefault()
      this.currentModal.focus()
      return
    }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  },

  getFocusableElements(modal) {
    return Array.from(modal.querySelectorAll(this.selectors.focusable)).filter(element => {
      return element.offsetParent !== null
    })
  },

  lockScroll() {
    this.scrollPosition = window.scrollY

    document.body.classList.add(this.classNames.bodyFixed)
    document.body.style.top = `-${this.scrollPosition}px`
  },

  unlockScroll() {
    document.body.classList.remove(this.classNames.bodyFixed)
    document.body.style.top = ''

    window.scrollTo({
      top: this.scrollPosition,
      behavior: 'auto',
    })
  },
}
